import { getLanguageLabel } from '../../../utils/HelperFunctions'
import { AllocationRow } from './LanguageAllocations.control'

// Pure transforms shared by the single-market LanguageAllocations editor
// (which commits each change to the backend) and the multi-market Add Market
// dialog (which holds allocations in local state until the market is created).
// Keeping them here means both surfaces share the exact same rounding /
// auto-balancing behaviour — every result sums to 1.0 within the backend's
// ±0.001 tolerance.

// Distributes 100% as evenly as possible across the supplied rows, with any
// rounding remainder going to the first row, so percentages always sum to
// exactly 1.0. Used whenever the number of rows changes (add / remove) — the
// per-row split is reset to even.
export const evenSplit = (rows: AllocationRow[]): AllocationRow[] => {
  const n = rows.length
  if (n === 0) return rows
  const base = Math.floor(100 / n)
  const remainder = 100 - base * n
  return rows.map((r, i) => ({
    ...r,
    percent: (base + (i === 0 ? remainder : 0)) / 100
  }))
}

// Adding a language re-splits the 100% evenly across all rows (e.g. one
// language at 100% → two at 50/50, three at 34/33/33).
export const addLanguage = (
  rows: AllocationRow[],
  language: string
): AllocationRow[] => evenSplit([...rows, { language, percent: 0 }])

// Re-even the split across whatever remains so percentages still sum to 100%
// (and a lone remaining row lands back at 100%).
export const removeLanguage = (
  rows: AllocationRow[],
  index: number
): AllocationRow[] => evenSplit(rows.filter((_, i) => i !== index))

export const changeLanguage = (
  rows: AllocationRow[],
  index: number,
  language: string
): AllocationRow[] => rows.map((a, i) => (i === index ? { ...a, language } : a))

// Auto-balance: the backend only accepts a split that sums to exactly 100%, so
// editing one row redistributes the remainder across the others. Clamp so the
// edited row keeps >= 1% and every other row can still hold >= 1% (backend
// requires every percent in (0, 1]). Remainder after integer division goes to
// the first of the other rows. `newPercentInt` is a whole-number percentage
// (0–100); rows store the 0.0–1.0 fraction.
export const changePercent = (
  rows: AllocationRow[],
  index: number,
  newPercentInt: number
): AllocationRow[] => {
  const otherCount = rows.length - 1
  if (otherCount <= 0) return rows // single allocation is always 100%
  const clamped = Math.max(1, Math.min(newPercentInt, 100 - otherCount))
  const remaining = 100 - clamped
  const base = Math.floor(remaining / otherCount)
  const rem = remaining - base * otherCount
  let otherIdx = 0
  return rows.map((a, i) => {
    if (i === index) return { ...a, percent: clamped / 100 }
    const give = base + (otherIdx === 0 ? rem : 0)
    otherIdx += 1
    return { ...a, percent: give / 100 }
  })
}

// The next language a researcher could add — the first supported language not
// already used by a row. Returns undefined when every supported language is
// already allocated.
export const nextAvailableLanguage = (
  rows: AllocationRow[],
  supportedLanguages: { code: string }[]
): string | undefined => {
  const used = new Set(rows.map((r) => r.language))
  return supportedLanguages.find((l) => !used.has(l.code))?.code
}

// Minimal shape of a getCountriesV2 row needed to resolve a country's
// languages — a multi-language country (e.g. Belgium) returns one row per
// locale, so filtering by countryCode yields every language it supports.
// `languageAllocationPercent` is the curated default split for that locale
// (renormalised server-side to sum to 1.0 across the country's locales);
// null for countries without a curated default.
type CountryLike = {
  countryCode: string
  locale: { language: string }
  languageAllocationPercent?: number | null
}

// The languages supported for a country, as { code, name } — the clean English
// label from `getLanguageLabel` (e.g. "French") rather than the supplier's
// `languageName` ("French (CA)").
export const supportedLanguagesForCountry = (
  countries: ReadonlyArray<CountryLike> | null | undefined,
  countryCode: string | undefined
): { code: string; name: string }[] => {
  if (!countryCode || !countries) return []
  return countries
    .filter((c) => c.countryCode === countryCode)
    .map((c) => ({
      code: c.locale.language,
      name: getLanguageLabel(c.locale.language)
    }))
}

// The default allocations for a newly-picked country: send ALL of the
// country's available languages. The backend no longer seeds defaults on
// create (cin-questionnaire #1242), so the dialog must forward the full set
// explicitly. If the country has a curated multi-language split (getCountriesV2
// `languageAllocationPercent`, e.g. Belgium nl .60 / fr .40) use those
// percentages, dominant language first so it becomes the market's primary
// locale; otherwise split evenly across all available languages (a
// single-language country lands at one language / 100%).
export const defaultAllocationsForCountry = (
  countries: ReadonlyArray<CountryLike> | null | undefined,
  countryCode: string | undefined
): AllocationRow[] => {
  if (!countryCode || !countries) return []
  const entries = countries.filter((c) => c.countryCode === countryCode)
  if (entries.length === 0) return []
  const preset = entries
    .filter((c) => c.languageAllocationPercent != null)
    .map((c) => ({
      language: c.locale.language,
      percent: c.languageAllocationPercent as number
    }))
    .sort((a, b) => b.percent - a.percent)
  if (preset.length > 0) return preset
  return evenSplit(
    entries.map((c) => ({ language: c.locale.language, percent: 0 }))
  )
}
